// src/services/companyService.ts

import {
  GetTokenWithPopupOptions,
  PopupConfigOptions
} from '@auth0/auth0-react';
import {
  Company,
  CreateCompanyInput,
  UpdateCompanyInput,
  ListAllCompaniesInput
} from '@/types/companyTypes'; // Adjust path as needed

// Define a more precise type for the getAccessTokenSilently function
type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;

// Update the type for getAccessTokenWithPopup
type GetAccessTokenWithPopupType = (
  options?: GetTokenWithPopupOptions,
  config?: PopupConfigOptions
) => Promise<string | undefined>;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const COMPANIES_ENDPOINT = `${API_BASE_URL}/companies`;

/**
 * Helper function to acquire Auth0 access token.
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
      // scope: "read:companies write:companies" // Add specific scopes if needed
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn('Silent token acquisition failed for CompanyService:', error);
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      console.log(
        'Falling back to interactive token acquisition for CompanyService...'
      );
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error(
          'Interactive token acquisition failed to return a token.'
        );
      }
      return token;
    }
    throw error; // Rethrow other errors
  }
};

/**
 * Creates a new company.
 * Corresponds to: POST /api/companies/
 * Includes X-User-Id header.
 */
export const createCompany = async (
  input: CreateCompanyInput,
  userId: string, // Added userId parameter
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Company> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(COMPANIES_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId // Added X-User-Id header
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error creating company:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to create company. Status: ${response.status}`
    );
  }
  return (await response.json()) as Company;
};

/**
 * Fetches the company associated with the current user.
 * Corresponds to: GET /api/companies/
 * Uses X-User-Id header.
 */
export const getUserCompany = async (
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Company | null> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(COMPANIES_ENDPOINT, {
    // Endpoint is /api/companies/
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    }
  });

  if (response.status === 404) {
    console.warn(`No company found for user ID '${userId}'.`);
    return null;
  }
  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error fetching user company:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to fetch user company. Status: ${response.status}`
    );
  }
  return (await response.json()) as Company;
};

/**
 * Fetches a list of all companies (admin/general purpose).
 * Corresponds to: GET /api/companies/all-companies
 * Includes X-User-Id header.
 */
export const listAllCompanies = async (
  input: ListAllCompaniesInput,
  userId: string, // Added userId parameter
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Company[]> => {
  // Assuming the backend returns a list of Company objects
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  // Construct query parameters if ListAllCompaniesInput has properties
  const queryParams = new URLSearchParams();
  queryParams.append('pageNumber', (input.pageNumber ?? 1).toString());
  queryParams.append('pageSize', (input.pageSize ?? 20).toString());
  if (input.nameFilter) queryParams.append('nameFilter', input.nameFilter);
  // Add other params from input as needed

  const url = `${COMPANIES_ENDPOINT}/all-companies${
    queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId // Added X-User-Id header
    }
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error fetching all companies:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to fetch all companies. Status: ${response.status}`
    );
  }
  return (await response.json()) as Company[];
};

/**
 * Updates an existing company's information.
 * Corresponds to: PUT /api/companies/update
 * Includes X-User-Id header.
 */
export const updateCompany = async (
  input: UpdateCompanyInput,
  userId: string, // Added userId parameter
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Company> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(`${COMPANIES_ENDPOINT}/update`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId // Added X-User-Id header
    },
    body: JSON.stringify(input)
  });

  if (response.status === 404) {
    const errorMsg = `Company with ID '${input.id}' not found for update.`;
    console.error(errorMsg);
    throw new Error(errorMsg);
  }
  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error updating company:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to update company. Status: ${response.status}`
    );
  }
  return (await response.json()) as Company;
};

/**
 * Creates a new company with default template and adds the current user as admin (super admin only).
 * Corresponds to: POST /api/companies/create-super-admin-company
 * Includes X-User-Id header.
 */
export const createSuperAdminCompany = async (
  companyName: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Company> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const input: CreateCompanyInput = {
    name: companyName
  };

  const response = await fetch(`${COMPANIES_ENDPOINT}/create-super-admin-company`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error creating super admin company:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to create company. Status: ${response.status}`
    );
  }
  return (await response.json()) as Company;
};
